/** Minimum versions after CSS/JS downleveling (see packages/web browserslist + vite.config). */
export const WEB_BROWSER_MIN_VERSIONS = {
  safari: { major: 15, minor: 0 },
  chrome: 100,
  firefox: 115,
  edge: 100,
} as const;

function isSafariUserAgent(userAgent: string): boolean {
  return /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|FxiOS/i.test(userAgent);
}

function parseSafariVersion(userAgent: string): { major: number; minor: number } | null {
  const match = userAgent.match(/Version\/(\d+)\.(\d+)/);
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]) };
}

function parseChromeVersion(userAgent: string): number | null {
  const match = userAgent.match(/(?:Chrome|CriOS)\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function parseFirefoxVersion(userAgent: string): number | null {
  const match = userAgent.match(/Firefox\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function parseEdgeVersion(userAgent: string): number | null {
  const match = userAgent.match(/Edg\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function isSafariAtLeast(
  version: { major: number; minor: number },
  minimum: { major: number; minor: number }
): boolean {
  return (
    version.major > minimum.major ||
    (version.major === minimum.major && version.minor >= minimum.minor)
  );
}

/**
 * Safari < 15.4 does not support the CSP worker-src directive (falls back to child-src).
 * Session Replay also logs cross-origin iframe access errors for Stripe and Firebase embeds.
 */
export function isLegacySafari(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua || !isSafariUserAgent(ua)) return false;

  const version = parseSafariVersion(ua);
  if (!version) return false;

  return !isSafariAtLeast(version, { major: 15, minor: 4 });
}

export function supportsSessionReplay(userAgent?: string): boolean {
  return !isLegacySafari(userAgent);
}

/**
 * Whether the browser can run the CourtSync web app (downleveled Tailwind CSS v4 + JS).
 */
export function isSupportedWebBrowser(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua) return true;

  if (/MSIE |Trident\//.test(ua)) return false;

  if (isSafariUserAgent(ua)) {
    const version = parseSafariVersion(ua);
    if (!version) return false;
    return isSafariAtLeast(version, WEB_BROWSER_MIN_VERSIONS.safari);
  }

  const edgeVersion = parseEdgeVersion(ua);
  if (edgeVersion !== null) {
    return edgeVersion >= WEB_BROWSER_MIN_VERSIONS.edge;
  }

  const chromeVersion = parseChromeVersion(ua);
  if (chromeVersion !== null) {
    return chromeVersion >= WEB_BROWSER_MIN_VERSIONS.chrome;
  }

  const firefoxVersion = parseFirefoxVersion(ua);
  if (firefoxVersion !== null) {
    return firefoxVersion >= WEB_BROWSER_MIN_VERSIONS.firefox;
  }

  return true;
}
